// ---------------------------------------------------------------------------
// Service monitoring — GET /monitoring_status.json and GET/POST
// /monitoring_config.json.
//
// Both endpoints are admin-only on EVERY method. Unlike /master.json, where a
// non-admin POST comes back as HTTP 200 carrying code: 1, these answer with a
// real 403 on GET and POST, so the HTTP status can be trusted.
//
// NEITHER GET may go through apiOperation. Both return a RAW object rather than
// the {errors:[...]} envelope, and the helper raises a global red toast for any
// non-OK response and turns a 401 into a forced logout — on a 15-second poll
// that is a toast storm plus a surprise logout the moment a session expires.
// Same rule /epg_status.json already follows. The SAVE is a user action and
// does go through apiOperation.
// ---------------------------------------------------------------------------

export const monitoringStatusUrl = "/monitoring_status.json";
export const monitoringConfigUrl = "/monitoring_config.json";

// The two "prove the SAVED settings work" buttons. Both take NO body — they
// always test what is stored, never what is in the form — and answer with the
// usual {errors:[...]} envelope, so they go through apiOperation.
export const monitoringTestAlertUrl = "/monitoring_test_alert.json";
export const monitoringTestTicketsUrl = "/monitoring_test_tickets.json";

// The server sweeps every 15 s; polling faster only costs authenticated
// requests, polling slower makes a transition look laggy.
export const MONITORING_POLL_MS = 15000;

// A completed sweep older than this while checks exist means the scheduler has
// stopped — the sweep interval is 15 s, so a minute is already four missed
// ticks.
export const SWEEP_STALE_MS = 60000;

// Server-side defaults, surfaced as placeholders so a blank field reads as
// "whatever the server does" instead of as zero.
export const REALERT_HOURS_DEFAULT		= 6;
export const CHECK_INTERVAL_DEFAULT		= 60;
export const CHECK_TIMEOUT_DEFAULT		= 30;
export const EXPECT_STATUS_DEFAULT		= 200;

// Kind-specific values the server enforces regardless of what is configured.
export const SKYWAY_PROBE_INTERVAL_DEFAULT	= 900;
export const SKYWAY_PROBE_TIMEOUT_MIN		= 65;

// ---------------------------------------------------------------------------
// GET /monitoring_status.json
// ---------------------------------------------------------------------------

// One row of the live registry.
//
// The three verdict flags are NOT redundant: `ok` is the current sample,
// `degraded` means "OK, but something upstream is failing right now" and
// `alerting` is the debounced verdict that email is derived from. Deriving the
// pill from `ok` alone is wrong — see checkTone in pages/Settings/monitoringUi.
//
// Every timestamp is OMITTED when unset; there is no zero-time sentinel to
// filter out. Normalised to "" here, so "" means "never".
export interface MonitoringCheck {
  check:				string;	// stable identity, e.g. "kpn:KPN-1:auth"
  kind:					string;	// one of the seven kinds; open enum on purpose
  ok:					boolean;
  degraded:				boolean;
  alerting:				boolean;
  pending:				boolean;	// has never once succeeded
  err:					string;
  detail:				string;
  latency_ms:			number;	// 0 for a PASSIVE check — render nothing
  consecutive_failures:	number;
  ran_at:				string;
  last_ok:				string;	// "" = never succeeded
  last_change:			string;	// when `alerting` last flipped
  last_alert_at:		string;	// "" in log-only mode, and always for `mailer`
}

export interface MonitoringStatus {
  checks:				MonitoringCheck[];	// never null; sorted by `check`
  last_sweep_at:		string;	// "" = no sweep has COMPLETED here
  // The machine name every alert carries. "" while monitoring has not started
  // on this server.
  hostname:				string;
  // The name the server uses RIGHT NOW — refreshed every 15 s, so it trails a
  // save. Never the source of the page's preview.
  server_name:			string;
  // `hostname` came in middleware 0.7.16ubuntu2 together with the masking of
  // the Teams / tickets secrets, and is always present from then on (even as
  // ""). An older server hands those keys back in clear, so the page offers
  // the channel editors only once it has seen this key.
  has_hostname:			boolean;
  log_drops:			number;	// lines dropped rather than block a request
  generated_at:			string;
}

function str(val: any): string {
  return typeof val === "string" ? val : "";
}

function num(val: any): number {
  return typeof val === "number" && isFinite(val) ? val : 0;
}

export function copyMonitoringCheck(raw: any): MonitoringCheck {
  return {
    check:					str(raw?.check),
    kind:					str(raw?.kind),
    ok:						!!raw?.ok,
    degraded:				!!raw?.degraded,
    alerting:				!!raw?.alerting,
    pending:				!!raw?.pending,
    err:					str(raw?.err),
    detail:					str(raw?.detail),
    latency_ms:				num(raw?.latency_ms),
    consecutive_failures:	num(raw?.consecutive_failures),
    ran_at:					str(raw?.ran_at),
    last_ok:				str(raw?.last_ok),
    last_change:			str(raw?.last_change),
    last_alert_at:			str(raw?.last_alert_at),
  };
}

export function copyMonitoringStatus(raw: any): MonitoringStatus {
  const checks = Array.isArray(raw?.checks) ? raw.checks : [];

  return {
    checks:			checks
    					.filter((c: any) => c && typeof c === "object")
    					.map(copyMonitoringCheck),
    last_sweep_at:	str(raw?.last_sweep_at),
    hostname:		str(raw?.hostname),
    server_name:	str(raw?.server_name),
    has_hostname:	isObject(raw) && "hostname" in raw,
    log_drops:		num(raw?.log_drops),
    generated_at:	str(raw?.generated_at),
  };
}

// ---------------------------------------------------------------------------
// GET/POST /monitoring_config.json
// ---------------------------------------------------------------------------

// A service the operator added by hand. `type` is "http" today; the index
// signature is load-bearing — see copyMonitoringSection.
export interface MonitoringCheckSpec {
  type:					string;
  name:					string;
  url:					string;
  method?:				string;	// GET | HEAD, default GET
  expect_status?:		number;	// default 200
  body_contains?:		string;	// never matches with HEAD — no body
  interval_seconds?:	number;	// default check_defaults.interval_seconds
  [key: string]:		any;
}

export interface MonitoringCheckDefaults {
  interval_seconds?:	number;
  timeout_seconds?:		number;
  [key: string]:		any;
}

// The `monitoring` section of config.json, VERBATIM. An absent section reads as
// {} — auto-derived checks, default cadences, log-only alerting — which is a
// normal state, not an error.
export interface MonitoringSection {
  server_name?:			string;
  alert_emails?:		string[];
  // SECRET. GET returns it with `sig=REDACTED` (or as just "REDACTED"); the
  // mask sent back unchanged keeps the stored value.
  teams_webhook?:		string;
  teams_disabled?:		boolean;
  teams_mentions?:		string[];
  tickets_url?:			string;
  // SECRET. GET returns "REDACTED"; kept only when sent back with the SAME
  // tickets_url.
  tickets_token?:		string;
  tickets_project?:		string;
  tickets_disabled?:	boolean;
  realert_hours?:		number;
  disabled?:			boolean;
  check_defaults?:		MonitoringCheckDefaults;
  overrides?:			any[];	// not editable here; round-trips untouched
  checks?:				MonitoringCheckSpec[];
  [key: string]:		any;
}

export interface MonitoringConfig {
  monitoring:			MonitoringSection;
  log_level:			string;	// "" = key absent, i.e. the built-in default
  log_format:			string;	// "" = key absent
}

function isObject(val: any): boolean {
  return !!val && typeof val === "object" && !Array.isArray(val);
}

// Spread FIRST, normalise second. POST replaces the WHOLE section (§8.1 of the
// wire contract), so every key this UI does not render still has to be here at
// save time or the save deletes it — including `overrides` and any key a newer
// server version added. This is the single easiest way to destroy an operator's
// settings, so the unknown keys are preserved by construction rather than by
// remembering to copy each one.
export function copyMonitoringCheckSpec(raw: any): MonitoringCheckSpec {
  const src = isObject(raw) ? raw : {};

  return {
    ...src,
    type:			str(src.type) || "http",
    name:			str(src.name),
    url:			str(src.url),
  };
}

export function copyMonitoringSection(raw: any): MonitoringSection {
  const src = isObject(raw) ? raw : {};

  const out: MonitoringSection = { ...src };

  // Every normalised-empty form below is semantically identical to the key
  // being absent, so adding it cannot change behaviour: [] recipients is
  // log-only mode, 0 hours is the built-in default, false is not-disabled.
  out.alert_emails =	Array.isArray(src.alert_emails)
  						?	src.alert_emails.filter((e: any) => typeof e === "string")
  						:	[];
  out.realert_hours =	num(src.realert_hours);
  out.disabled =		!!src.disabled;

  out.check_defaults =	{
    ...(isObject(src.check_defaults) ? src.check_defaults : {}),
    interval_seconds:	num(src.check_defaults?.interval_seconds),
    timeout_seconds:	num(src.check_defaults?.timeout_seconds),
  };

  out.checks =	Array.isArray(src.checks)
  				?	src.checks.filter(isObject).map(copyMonitoringCheckSpec)
  				:	[];

  // `overrides` is deliberately NOT normalised — nothing here edits it, and the
  // spread above already carries it through the round trip byte-compatible.

  return out;
}

export function copyMonitoringConfig(raw: any): MonitoringConfig {
  // The bare section (no `monitoring` wrapper) is also accepted by POST, but
  // GET always wraps, so read the wrapper and fall back to {} rather than
  // guessing that a wrapper-less body is the section.
  return {
    monitoring:		copyMonitoringSection(raw?.monitoring),
    log_level:		str(raw?.log_level),
    log_format:		str(raw?.log_format),
  };
}

// ---------------------------------------------------------------------------
// POST body
// ---------------------------------------------------------------------------

// The two logging keys follow DIFFERENT rules from the section, and the
// difference is deliberate:
//
//   monitoring  — replace. Absent means "delete the section".
//   log_level   — patch.   Absent means "leave the stored level alone";
//                          "" means "remove the key, back to the default".
//
// So an unchanged level is OMITTED rather than sent back as its resolved value:
// sending "info" for a level that is currently *absent* would pin the key to a
// value the operator never chose, and the file would stop tracking the built-in
// default.
export interface MonitoringSavePayload {
  monitoring:			MonitoringSection;
  log_level?:			string;
  log_format?:			string;
}

export function buildMonitoringPayload(
  section: MonitoringSection,
  logLevel?: string,
  logFormat?: string,
): MonitoringSavePayload {
  const payload: MonitoringSavePayload = { monitoring: section };

  if (logLevel !== undefined)
    payload.log_level = logLevel;

  if (logFormat !== undefined)
    payload.log_format = logFormat;

  return payload;
}
